import type { SocialMedallionWidget } from './SocialMedallionWidget';
import {
    TimelineSlot,
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import { z } from 'zod';
import { SocialMedallionColors, Vendors } from './enums';
import {
    zMarshalToBoolean,
    zMarshalToStrictInt,
    zMarshalToStrictIntNullable,
} from '../../ReachApi/Marshallers';

export type SocialMedallionTimelineSlotParams = {
    thisloopcount: number;
    stackPage?: StackPageObject;
    post?: SocialMediaPost;
    totalposts?: number;
};

export type SocialMedallionTimelineSlot = TimelineSlot<
    SocialMedallionTimelineSlotParams,
    SocialMedallionWidget
>;

export const ZodSocialMediaWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend(
    {
        smid: z.string(),
        vendor: z.string(),
        postnum: z.string(),
        timezone: z.string(),
        profilepic: z.string(),
        postinterval: z.string(),
        checkbackinterval: z.string(),
        images_only: z.unknown().transform(zMarshalToBoolean),
        reach_profile_id: z.string().min(1),
        template: z.string().min(1),
        template_version: z.string().nullish(),
        text_only: z.unknown().transform(zMarshalToBoolean),
        stacking: z.unknown().transform(zMarshalToBoolean),
    }
);

export type SocialMediaWidgetConfigurationDetails = z.infer<
    typeof ZodSocialMediaWidgetConfigurationDetails
>;

export const ZodSocialMediaWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.MEDALLION),
    config: ZodSocialMediaWidgetConfigurationDetails,
});

export type SocialMediaWidgetConfiguration = z.infer<typeof ZodSocialMediaWidgetConfiguration>;

/*
 * Theses should be string but some posts return
 * PHP DateTime object erroneously
 * @todo: convert to string
 */
const PHPDateTime = z.object({
    date: z.string(),
    timezone_type: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    timezone: z.string(),
});
type PHPDateTime = z.infer<typeof PHPDateTime>;

export const ZodFacebookPost = z.object({
    caption: z.string().nullable(),
    created: z.string(),
    created_time: z.string(),
    current_generation: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    date: z.string(),
    description: z.string().nullable(),
    facebook_profiles_id: z.string(),
    from_facebook_id: z.string(),
    from_name: z.string(),
    icon: z.string().nullable(),
    id: z.string().optional(),
    link: z.string(),
    message: z.string().nullable(),
    name: z.string(),
    object_id: z.string().nullable(),
    picture: z.string().nullable(),
    post_id: z.string(),
    source: z.string(),
    status_type: z.string(),
    type: z.string(),
    updated_time: z.string(),
    approval_status: z.string().optional(),
    post_status: z.string().optional(),
});

export const ZodTwitterPost = z.object({
    id: z.string(),
    edit_history_tweet_ids: z.array(z.string()),
    text: z.string(),
    created_at: z.string(),
    handle: z.string(),
    profile_image_url: z.string().nullish(),
    image_url: z.string().nullable(),
    mediaUrls: z.array(z.string()),
    mediaIds: z.array(z.string()),
});

export const ZodInstagramPost = z.object({
    caption_text: z.string().nullable(),
    created: z.string(),
    date: z.string(),
    id: z.string(),
    image_thumbnail_url: z.string(),
    image_url: z.string().nullish(),
    instagram_profiles2_id: z.string(),
    internal_id: z.string(),
    location: z.string().nullable(),
    media_url: z.string().nullable(),
    profile_fullname: z.string(),
    profile_picture: z.string(),
    profile_username: z.string(),
    type: z.string(),
    video_url: z.string().nullable(),
    post_status: z.string().optional(),
    approval_status: z.string().optional(),
});

export const ZodInstagramBusinessPost = z.object({
    caption: z.string().nullable(),
    children: z.string().nullish(),
    comments_count: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    created: z.string(),
    date: z.string(),
    hash: z.string().optional(),
    id: z.string(),
    like_count: z.union([z.string(), z.number()]).nullish().transform(zMarshalToStrictIntNullable),
    media_type: z.string(),
    media_url: z.string().nullable(),
    owner_id: z.string().nullable(),
    permalink: z.string(),
    profile_picture: z.string(),
    timestamp: z.nullable(z.union([PHPDateTime, z.string()])), //Terrible but looks like it could be either
    username: z.string(),
    post_status: z.string().optional(),
    approval_status: z.string().optional(),
});

export const ZodWorkplacePost = z.object({
    created: z.string(),
    created_time: z.string(),
    date: z.string(),
    from_id: z.string(),
    from_name: z.string(),
    group_id: z.string(),
    hash: z.string().optional(),
    id: z.string().optional(),
    message: z.string().nullable(),
    object_id: z.string().nullable(),
    post_id: z.string(),
    system_created_time: PHPDateTime,
    type: z.string(),
    updated_time: z.string(),
    workplace_groups_id: z.string(),
    approval_status: z.string().optional(),
    post_status: z.string().optional(),
});

export const ZodSlackPost = z.object({
    created: z.string(),
    id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    slack_channel_id: z.union([z.string(), z.number()]).transform(zMarshalToStrictInt),
    type: z.string(),
    slack_type: z.string(), // message type in slack
    slack_sub_type: z.string().nullable(), // message sub type in slack
    resource_url: z.string().nullable(), // private image url
    resource_id: z.string().nullable(),
    resource_name: z.string().nullable(),
    resource_title: z.string().nullable(),
    thumbnail: z.string(),
    message: z.string().nullable(),
    comment: z.string().nullable(),
    user: z.string(),
    name: z.string().nullable(),
    profile_picture: z.string().nullable(),
    date: z.string(),
    slack_ts: z.string(),
    approval_status: z.string().optional(),
    post_status: z.string().optional(),
});

/*
 * Types of raw data from API
 */
export type RawFacebookPost = z.infer<typeof ZodFacebookPost>;
export type RawTwitterPost = z.infer<typeof ZodTwitterPost>;
export type RawInstagramPost = z.infer<typeof ZodInstagramPost>;
export type RawInstagramBusinessPost = z.infer<typeof ZodInstagramBusinessPost>;
export type RawWorkplacePost = z.infer<typeof ZodWorkplacePost>;
export type RawSlackPost = z.infer<typeof ZodSlackPost>;

export type RawSocialMediaPost =
    | RawFacebookPost
    | RawTwitterPost
    | RawInstagramPost
    | RawInstagramBusinessPost
    | RawWorkplacePost
    | RawSlackPost;

/**
 * Type of parsed posts
 */

type BaseSocialMediaPost = {
    id: string;
    date: string;
    formattedDate: string;
    posttype: string | null;
    message: string | null;
    image: string | null | undefined;
    postElement?: HTMLDivElement | null;
    sm: Vendors;
    template: string;
};

export type FacebookPost = BaseSocialMediaPost & {
    facebook: true;
    profilepic: string;
    sm: Vendors.Facebook;
    xtramedia: null;
    by: string;
    color: string;
};

export type TwitterPost = BaseSocialMediaPost & {
    twitter: true;
    profilepic: string;
    sm: Vendors.Twitter;
    xtramedia: null;
    by: string;
    color: SocialMedallionColors.Twitter;
    mediaUrls: Array<string>;
    mediaIds: Array<string>;
};

export type InstagramPost = BaseSocialMediaPost & {
    instagram: true;
    profilepic: string;
    sm: Vendors.Instagram;
    xtramedia: null;
    by: string;
    color: SocialMedallionColors.Instagram;
};

export type InstagramBusinessPost = BaseSocialMediaPost & {
    instagram: true;
    profilepic: string | null;
    // use Instagram because the template is same for both Instagram and Instagram Business
    sm: Vendors.Instagram;
    xtramedia: null;
    by: string;
    color: SocialMedallionColors.InstagramBusiness;
};

export type WorkplacePost = BaseSocialMediaPost & {
    workplace: true;
    profilepic: string;
    sm: Vendors.Workplace;
    xtramedia: null;
    by: string;
    color: SocialMedallionColors.Workplace;
};

export type SlackPost = BaseSocialMediaPost & {
    slack: true;
    profilepic: string | null;
    sm: Vendors.Slack;
    xtramedia: null;
    by: string | null;
    color: SocialMedallionColors.Slack;
};

export type SocialMediaPost =
    | FacebookPost
    | TwitterPost
    | InstagramPost
    | InstagramBusinessPost
    | WorkplacePost
    | SlackPost;

export type StackPageObject = {
    id: number;
    xtramedia: null;
};

type RenderedSocialMediaPostItem = SocialMediaPost & { fontResize: boolean };

export type SocialMediaRenderContext = {
    wid: string;
    wobj: {
        widget: {
            widgetsid: string;
            config: {
                css: string;
            };
        };
    };
    bodywidth: number;
    posts: RenderedSocialMediaPostItem[];
    Zone: {
        Id: number;
    };
    zone: {
        id: number;
    };
    widget: {
        id: number;
    };
    Widget: {
        Id: number;
    };
    medallion: {
        textOnly: 'true' | 'false';
        picsOnly: 'true' | 'false';
    };
    metadata: {
        landscape: boolean;
        fontResize: false;
        fontProperties: {
            minSize: number;
            maxSize: number;
            maxTextHeight: number | null;
            ellipsis: true;
        };
    };
};
